import cx from 'classnames';
import debounce from 'lodash/debounce';
import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventListener } from 'usehooks-ts';

import useAssistantActionHandlers from '../../../common/use-assistant-action-handlers';
import useElementSize from '../../../common/use-element-size';
import { trackAssistantIntegrationLinkClick } from '../../../models/assistant/analytics';
import { AssistantResponse } from '../../../models/assistant/interfaces/AssistantResponse';
import { Service } from '../../../models/assistant/interfaces/subject/Service';
import { ShortcutsHomepageModule } from '../../../models/home-page-custom/types';
import { Button } from '../../ui';
import ShortcutButton from './shortcut-button/shortcut-button';
import './homepage-shortcuts.scss';
import { useLinkNavigation } from '../../../common/use-link-navigation';
import useScreenSize from '../../../common/use-screen-size';
import { useDispatch } from 'react-redux';
import { uiOperations } from '../../../models/ui';
import { ID as ShortcutMobileListModalId } from '../../ui/shortcut/modal/shortcut-mobile-list-modal';
import useFeatureFlag from '../../../common/use-feature-flag';
import { Feature } from '../../../models/features/features';
import { useRecoilState } from 'recoil';
import { shortcutHeaderOpen } from '../../../models/home-page/recoil-state';
import { useIntegrationsQuery } from '../../../common/queries/use-assistant-query';
import {
  Alert,
  Button as SolarButton,
  IconButton,
  ModalActionButton,
  ModalContent,
  ModalContentActions,
  ModalContentBody,
  ModalContentFooter,
  ModalContentHeader,
  ModalOverlay,
  ModalPortal,
  ModalRoot,
} from '@socialchorus/shared-ui-components';
import { EditShortcutOrderModal } from './edit-shortcut-order-modal/edit-shortcut-order-modal';

const CONTAINER_MAX_HEIGHT = 102;

export function applyShortcutOrder<T extends { id: string }>(
  integrations: T[],
  shortcutOrder?: number[]
): T[] {
  if (!shortcutOrder || shortcutOrder.length === 0) return integrations;

  const orderMap = new Map(shortcutOrder.map((id, idx) => [String(id), idx]));
  return [...integrations].sort((a, b) => {
    const aIdx = orderMap.get(a.id) ?? Infinity;
    const bIdx = orderMap.get(b.id) ?? Infinity;
    return aIdx - bIdx;
  });
}

export function splitByPinned<T extends { subject?: { pinned?: boolean } }>(
  items: T[]
): { pinned: T[]; nonPinned: T[] } {
  const pinned = items.filter((i) => i.subject?.pinned);
  const nonPinned = items.filter((i) => !i.subject?.pinned);
  return { pinned, nonPinned };
}

type ShortcutItemProps = {
  integration: AssistantResponse<Service>;
  className?: string;
  onAfterClick?: () => void;
};

const ShortcutItem = ({
  integration,
  className,
  onAfterClick,
}: ShortcutItemProps) => {
  const { handleAction } = useAssistantActionHandlers();
  const getLinkNavigation = useLinkNavigation();

  const isTLC = integration?.subject?.integrationType === 'link';
  const integrationActions = integration?.subject?.buttons;
  const directAction = isTLC && integrationActions?.length === 1;

  const onClick = directAction
    ? () => {
        const button = integrationActions[0];
        trackAssistantIntegrationLinkClick({
          service_id: integration.id,
          service_name: integration.subject.title,
          link_name: button.button_text,
          order: 0,
        });
        handleAction(integrationActions[0]?.action);
        onAfterClick?.();
      }
    : onAfterClick;

  const href = directAction
    ? undefined
    : getLinkNavigation(`/assistant/service/${integration?.id}`);

  return (
    <ShortcutButton
      id={integration?.id}
      iconSrc={integration?.subject?.image_url}
      label={integration?.name}
      href={href}
      onClick={onClick}
      className={className}
    />
  );
};

type HomepageShortcutsProps = {
  topIsFlush?: boolean;
  shortcutsModule?: ShortcutsHomepageModule;
  homepageId?: string;
};

// topIsFlush: when true, the top corners are not rounded, as this means the top of the shortcut container is flush with the top of its container.
const HomepageShortcuts = ({
  topIsFlush = true,
  shortcutsModule,
  homepageId,
}: HomepageShortcutsProps) => {
  const { t } = useTranslation();
  const { isMobile } = useScreenSize();
  const dispatch = useDispatch();

  const displayTitle =
    shortcutsModule?.attributes.name || (t('navigation.shortcuts') as string);

  const notificationCenterEnabled = useFeatureFlag(
    Feature.NOTIFICATION_CENTER_ENABLED
  );
  const shortcutsModuleEnabled = useFeatureFlag(
    Feature.CUSTOM_HOMEPAGE_SHORTCUTS_MODULE_ENABLED
  );

  const {
    data: integrations,
    isError,
    isRefetching,
    isOldData,
  } = useIntegrationsQuery();

  const [isOpen, setIsOpen] = useRecoilState(shortcutHeaderOpen);
  const [showMoreIcon, setShowMoreIcon] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [allShortcutsOpen, setAllShortcutsOpen] = useState(false);
  const [setContainerRef, { scrollHeight }] = useElementSize();

  const allowReordering =
    shortcutsModuleEnabled &&
    !!shortcutsModule?.attributes.settings?.allow_user_reordering;
  const currentOrder =
    shortcutsModule?.attributes.user_customization?.shortcut_order;
  const currentHiddenShortcuts =
    shortcutsModule?.attributes.user_customization?.hidden_shortcuts;

  const orderedIntegrations = useMemo(() => {
    const { pinned, nonPinned } = splitByPinned(integrations ?? []);
    return [...pinned, ...applyShortcutOrder(nonPinned, currentOrder)];
  }, [integrations, currentOrder]);

  const visibleIntegrations = useMemo(() => {
    if (!currentHiddenShortcuts || currentHiddenShortcuts.length === 0) {
      return orderedIntegrations;
    }
    // Use numeric comparison to match the integer IDs from the API.
    // Pinned shortcuts are always shown regardless of hidden_shortcuts.
    const hiddenSet = new Set(currentHiddenShortcuts);
    return orderedIntegrations.filter(
      (i) => !hiddenSet.has(Number(i.id)) || i.subject?.pinned
    );
  }, [orderedIntegrations, currentHiddenShortcuts]);

  const containerClasses = cx('shortcuts-grid', {
    open: isOpen,
  });
  const shortcutClasses = cx('shortcut');
  const shortcutsContainerClasses = cx('shortcuts', {
    'shortcuts--not-flush-top': !topIsFlush,
  });

  const moreIcon = isOpen ? 'expand_less' : 'expand_more';
  const isContainerOverflowing = scrollHeight > CONTAINER_MAX_HEIGHT;

  const debouncedSetShowMoreIconWhenOverflowing = useCallback(() => {
    return debounce(() => {
      setShowMoreIcon(isContainerOverflowing);
    }, 250);
  }, [isContainerOverflowing]);

  useLayoutEffect(() => {
    setShowMoreIcon(isContainerOverflowing);
  }, [isContainerOverflowing]);

  useEventListener('resize', debouncedSetShowMoreIconWhenOverflowing);

  const handleMoreClick = () => {
    if (shortcutsModuleEnabled) {
      setAllShortcutsOpen(true);
    } else if (isMobile && notificationCenterEnabled && !isOpen) {
      dispatch(uiOperations.displayOverlay(ShortcutMobileListModalId));
    } else {
      setIsOpen((o) => !o);
    }
  };
  // If data is "Fresh" (less than 1 hour old): show and update when it comes back
  // If data is "Old": show skeleton and update when it comes back ( either way to show cache or fresh)
  // If there is no data: show nothing until we know that there are shortcuts ( it comes back)
  if (!integrations || integrations.length === 0) {
    return null;
  }

  if (isOldData && isRefetching) {
    return <ShortcutSkeleton />;
  }

  return (
    <nav className={shortcutsContainerClasses} aria-label={displayTitle}>
      {isError && (
        <div className="shortcuts-alert">
          <Alert
            type="warning"
            size="compact"
            title="Some Shortcuts may be temporarily unavailable."
          />
        </div>
      )}
      {shortcutsModuleEnabled && (
        <div className="shortcuts-header">
          <span className="text-body-bold text-black-90">{displayTitle}</span>
          {allowReordering && shortcutsModule && (
            <IconButton
              iconName="settings"
              size="compact"
              aria-label={t('shortcuts.edit_order', { name: displayTitle })}
              onClick={() => setEditModalOpen(true)}
            />
          )}
          {showMoreIcon && (
            <SolarButton
              variant="text"
              size="compact"
              label={t('shortcuts.view_all')}
              onClick={handleMoreClick}
              className="shortcuts__view-all"
            />
          )}
        </div>
      )}
      <div className="shortcuts-container">
        <div
          className={containerClasses}
          ref={setContainerRef}
          data-testid="shortcuts-grid"
        >
          {visibleIntegrations.map((integration) => (
            <ShortcutItem
              key={integration?.id}
              integration={integration}
              className={shortcutClasses}
            />
          ))}
        </div>
        {!shortcutsModuleEnabled && (showMoreIcon || isOpen) && (
          <Button
            className={'shortcuts__more-icon'}
            theme={'icon-only'}
            icon={moreIcon}
            onClick={handleMoreClick}
          />
        )}
      </div>
      {allowReordering && homepageId && shortcutsModule && editModalOpen && (
        <EditShortcutOrderModal
          open
          onOpenChange={setEditModalOpen}
          shortcuts={integrations as AssistantResponse<Service>[]}
          homepageId={homepageId}
          moduleId={shortcutsModule.id}
          currentOrder={currentOrder}
          currentHiddenShortcuts={currentHiddenShortcuts}
          moduleName={displayTitle}
        />
      )}
      {shortcutsModuleEnabled && (
        <ModalRoot
          open={allShortcutsOpen}
          onOpenChange={setAllShortcutsOpen}
          mode="dialog"
        >
          <ModalPortal>
            <ModalOverlay />
            <ModalContent size="medium">
              <ModalContentHeader
                title={t('navigation.all_shortcuts', { name: displayTitle })}
              />
              <ModalContentBody>
                <div className="shortcuts-all-modal__grid">
                  {visibleIntegrations.map((integration) => (
                    <ShortcutItem
                      key={integration?.id}
                      integration={integration}
                      className={shortcutClasses}
                      onAfterClick={() => setAllShortcutsOpen(false)}
                    />
                  ))}
                </div>
              </ModalContentBody>
              <ModalContentFooter>
                <ModalContentActions>
                  <ModalActionButton
                    label={t('common.close')}
                    onClick={() => setAllShortcutsOpen(false)}
                  />
                </ModalContentActions>
              </ModalContentFooter>
            </ModalContent>
          </ModalPortal>
        </ModalRoot>
      )}
    </nav>
  );
};

const ShortcutSkeleton = () => {
  // Display multiple skeleton items to mimic the shortcut tray
  const skeletonItems = Array.from({ length: 6 }).map((_, index) => (
    <div className="shortcut-skeleton__item" key={`skeleton-${index}`}>
      <div className="shortcut-skeleton__icon" />
      <div className="shortcut-skeleton__label" />
    </div>
  ));

  return (
    <nav className="shortcuts" aria-label="Loading shortcuts">
      <div className="shortcuts-container shortcut-skeleton">
        {skeletonItems}
      </div>
    </nav>
  );
};

export default HomepageShortcuts;
